import React, { useEffect, useState } from 'react';
import { FiAlertTriangle, FiRefreshCw, FiX, FiXCircle } from 'react-icons/fi';
import { api } from '../../services/api';

const MIN_REASON_LENGTH = 3;
const MAX_REASON_LENGTH = 1000;

/**
 * Saisie obligatoire de la raison du refus d'un compte déménageur.
 * Le déménageur reçoit la raison (notification + e-mail) et peut corriger son inscription.
 */
function RejectMoverVerificationModal({ mover, onClose, onRejected }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setReason('');
    setError(null);
    setSubmitting(false);
  }, [mover?.id]);

  if (!mover) return null;

  const moverName = [mover.first_name, mover.last_name].filter(Boolean).join(' ') || 'ce déménageur';
  const trimmed = reason.trim();
  const canSubmit = trimmed.length >= MIN_REASON_LENGTH && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      setSubmitting(true);
      setError(null);
      const res = await api.rejectDemenageurAccount(mover.id, trimmed);
      if (!res?.success) {
        throw new Error(res?.message || 'Échec du refus');
      }
      onRejected?.(res.data);
    } catch (err) {
      setError(err.message || 'Erreur lors du refus');
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={() => !submitting && onClose?.()}
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-2xl border border-red-700/60 bg-gray-900 shadow-2xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-800 p-5">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-red-900/40 border border-red-700/60">
              <FiXCircle className="h-5 w-5 text-red-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Refuser la validation</h2>
              <p className="mt-1 text-sm text-gray-400">
                Compte de <span className="font-semibold text-gray-200">{moverName}</span>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-800 hover:text-white disabled:opacity-50"
            aria-label="Fermer"
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 p-5">
          <label className="block">
            <span className="text-sm font-semibold text-gray-200">
              Raison du refus <span className="text-red-400">*</span>
            </span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, MAX_REASON_LENGTH))}
              rows={5}
              autoFocus
              placeholder="Ex. : la photo du permis (verso) est floue, merci de la reprendre."
              className="mt-2 w-full resize-y rounded-xl border border-gray-700 bg-gray-950 px-4 py-3 text-sm text-white placeholder-gray-500 focus:border-red-500 focus:ring-2 focus:ring-red-500"
            />
            <span className="mt-1 block text-right text-xs text-gray-500">
              {reason.length}/{MAX_REASON_LENGTH}
            </span>
          </label>

          <p className="flex items-start gap-2 rounded-xl border border-amber-700/40 bg-amber-900/20 p-3 text-xs text-amber-200">
            <FiAlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            Le déménageur verra cette raison dans l'application (et par e-mail) et pourra modifier son
            formulaire d'inscription puis le renvoyer.
          </p>

          {error && (
            <p className="rounded-xl border border-red-700/50 bg-red-900/30 p-3 text-sm text-red-300">{error}</p>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-800 p-5">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-xl border border-gray-600 bg-gray-800 px-4 py-2 text-sm font-medium text-gray-200 hover:bg-gray-700 disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? <FiRefreshCw className="h-4 w-4 animate-spin" /> : <FiXCircle className="h-4 w-4" />}
            {submitting ? 'Envoi…' : 'Refuser le compte'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default RejectMoverVerificationModal;
